import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Col, Radio, Row, Select, Switch } from "antd";
import {
    filter,
    keys,
    map,
    pluck,
    uniq,
    includes,
    sortBy,
    chain,
} from "underscore";
import WidgetModuleLayout from "./WidgetModuleLayout";
import {
    CommonChartAttributes,
    CommonWidgetMetrics,
    CrossChannelTableDataModules,
    IMyProductTrendsOriginalData,
    IPricingCompetitiveAnalysisOriginalData,
    IPricingCompetitiveAnalysisTrendsOriginalData,
    IProductAnalysisPricingModule,
    ITrendsWithComparisionWidgetFormatterKeys,
    MyProductTrendsModules,
    MyProductTrendsTypes,
    PricingCharts,
    ProductDeepdiveModules,
    TCrossChannelTableData,
} from "@ts/common-types";
import {
    TrendsWithCompareModalWidget,
    SummaryWidget,
    CompetitiveProductsDaywiseTrends,
    PriceVsSalesWidget,
    MsVsRpiTrends,
    PriceAndDealsCompetitionWidget,
    SalesVsPriceCompetitiveAnalysisWidget,
    SatisfactionDriversSummaryWidget,
    PricingAndDealCompetitionAnalysisWidget,
} from "@components/widgets";
import {
    chartToggleFiltersReducer,
    chartFiltersReducer,
    formatMsVsRpiData,
    formatPriceVsSalesData,
    formatTrendsWithCompareModalData,
    formatPriceDealsData,
    formatMyProductTrends,
    formatPricingCompetitiveAnalysisTrends,
    formatPricingCompetitiveAnalysis,
} from "@ts/widget";
import WidgetCard from "@components/widgets/WidgetCard";
import {
    PRICING_CHART_TOGGLE_FILTERS_INTIAL_STATE,
    PRICING_CHART_FILTERS_INTIAL_STATE,
    pricingFiltersOptions,
} from "@ts/constants";
import { showToast } from "@ts/modals";
import { useCrossChannelTableData } from "@/hooks/AllCustomHooks";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";

const ProductAnalysisPricingModule: React.FC<IProductAnalysisPricingModule> = ({
    loadingState,
    selectedSKU,
    pricingData,
    channel,
    currencySymbol,
}) => {
    const [showPricingAndDealsTrends, setShowPricingAndDealsTrends] =
        useState(false);
    const [chartToggleFilters, setChartToggleFilters] = useReducer(
        chartToggleFiltersReducer,
        PRICING_CHART_TOGGLE_FILTERS_INTIAL_STATE
    );
    const [chartFilters, setChartFilters] = useReducer(
        chartFiltersReducer,
        PRICING_CHART_FILTERS_INTIAL_STATE
    );

    const onChangePricingCompetitiveAnalysis = (value: string[]) => {
        if (value.length > 5) {
            showToast("danger", "You can select maximum 5 products");
            return;
        }
        setChartFilters({
            type: PricingCharts.PRICE_DEALS_SKUS,
            payload: value,
        });
    };

    const priceVsSalesData = useMemo(
        () =>
            ({
                [CommonChartAttributes.DEFAULT_DATA]: formatPriceVsSalesData(
                    pricingData.priceVsSales
                ),
                [CommonChartAttributes.PPU_DATA]: formatPriceVsSalesData(
                    pricingData.ppuPriceVsSales
                ),
            }[
                chartToggleFilters[PricingCharts.PRICE_SALES_PPU]
                    ? CommonChartAttributes.PPU_DATA
                    : CommonChartAttributes.DEFAULT_DATA
            ]),
        [pricingData, chartToggleFilters[PricingCharts.PRICE_SALES_PPU]]
    );

    const msVsRpiData = useMemo(() => {
        const filter = chartFilters[PricingCharts.MS_RPI];
        return {
            [CommonChartAttributes.DEFAULT_DATA]: formatMsVsRpiData(
                pricingData.msVsRpi,
                filter
            ),
            [CommonChartAttributes.PPU_DATA]: formatMsVsRpiData(
                pricingData.ppuMsVsRpi,
                filter
            ),
        }[
            chartToggleFilters[PricingCharts.MS_RPI_PPU]
                ? CommonChartAttributes.PPU_DATA
                : CommonChartAttributes.DEFAULT_DATA
        ];
    }, [
        pricingData,
        chartToggleFilters[PricingCharts.MS_RPI_PPU],
        chartFilters[PricingCharts.MS_RPI],
    ]);

    const skuFilter = (
        dataToFilter:
            | IPricingCompetitiveAnalysisOriginalData
            | IPricingCompetitiveAnalysisTrendsOriginalData[],
        competitonSku: string[]
    ) =>
        filter(
            dataToFilter,
            (data) =>
                data.sku === selectedSKU || includes(competitonSku, data.sku)
        );

    const excludeSelectedSku = (
        dataToFilter:
            | IPricingCompetitiveAnalysisOriginalData
            | IPricingCompetitiveAnalysisTrendsOriginalData[]
    ) => filter(dataToFilter, (data) => data.sku !== selectedSKU);

    const pricingCompetitiveAnalysisData = useMemo(() => {
        const competitonSku = chartFilters[PricingCharts.PRICE_DEALS_SKUS];
        const type = chartToggleFilters[PricingCharts.PRODUCT_COMPETITION_PPU]
            ? CommonChartAttributes.PPU_DATA
            : CommonChartAttributes.DEFAULT_DATA;
        const skusWithTitle = pricingData.myProductPriceAndDeal
            ? chain(pricingData.myProductPriceAndDeal)
                  .filter((item) => item.sku !== selectedSKU)
                  .uniq((item) => item.sku)
                  .map((item) => ({ sku: item.sku, sku_title: item.sku_title }))
                  .value()
            : [];

        const skus = map(skusWithTitle, (sku) => sku.sku);

        return {
            skus,
            [CommonChartAttributes.SKU_OPTIONS]: skus
                ? map(skusWithTitle, (data) => ({
                      value: data.sku,
                      label: `${data.sku_title.substring(0, 27)}-[${data.sku}]`,
                  }))
                : [],
            [CommonChartAttributes.BUBBLE_CHART]: {
                [CommonChartAttributes.DEFAULT_DATA]:
                    formatPricingCompetitiveAnalysis(
                        skuFilter(
                            pricingData.competitiveAnalysis,
                            competitonSku
                        )
                    ),
                [CommonChartAttributes.PPU_DATA]:
                    formatPricingCompetitiveAnalysis(
                        skuFilter(
                            pricingData.ppuCompetitiveAnalysis,
                            competitonSku
                        )
                    ),
            }[type],
            [CommonChartAttributes.TREND]: {
                [CommonChartAttributes.DEFAULT_DATA]:
                    formatPricingCompetitiveAnalysisTrends(
                        skuFilter(
                            pricingData.myProductPriceAndDeal,
                            competitonSku
                        ),
                        selectedSKU
                    ),
                [CommonChartAttributes.PPU_DATA]:
                    formatPricingCompetitiveAnalysisTrends(
                        skuFilter(
                            pricingData.ppuMyProductPriceAndDeal,
                            competitonSku
                        ),
                        selectedSKU
                    ),
            }[type],
        };
    }, [
        pricingData,
        chartToggleFilters[PricingCharts.PRODUCT_COMPETITION_PPU],
        chartFilters[PricingCharts.PRICE_DEALS_SKUS],
    ]);

    const pricingAndDealsData = useMemo(() => {
        const formatterkeys: ITrendsWithComparisionWidgetFormatterKeys = {
            categoryKey: CommonWidgetMetrics.DATE,
            yAxisKey: CommonWidgetMetrics.PRICE,
            yAxisOppositeKey: CommonWidgetMetrics.DEAL,
        };
        return {
            [CommonChartAttributes.DEFAULT_DATA]:
                formatTrendsWithCompareModalData(
                    formatPriceDealsData(pricingData.pricingAndDealsTrends),
                    formatterkeys
                ),
            [CommonChartAttributes.PPU_DATA]: formatTrendsWithCompareModalData(
                formatPriceDealsData(pricingData.ppuPricingAndDealsTrends),
                formatterkeys
            ),
        }[
            chartToggleFilters[PricingCharts.PRICING_DEALS_PPU]
                ? CommonChartAttributes.PPU_DATA
                : CommonChartAttributes.DEFAULT_DATA
        ];
    }, [pricingData, chartToggleFilters[PricingCharts.PRICING_DEALS_PPU]]);

    const crossChannelTableData = useCrossChannelTableData(
        pricingData?.myProductTrendsChartCrossChannelTableData,
        excludeSelectedSku(
            pricingData?.myProductTrendsChartCrossChannelExpandableTableData
        ),
        CrossChannelTableDataModules.PRICING
    );

    const competitionTableData = useCrossChannelTableData(
        pricingData?.myProductTrendsCompetitionTableData,
        excludeSelectedSku(
            pricingData?.myProductTrendsCompetitionExpandableTableData
        ),
        CrossChannelTableDataModules.PRICING
    );

    const myProductTrendsData = useMemo(() => {
        const competitonSku =
            chartFilters[PricingCharts.PRICE_DEALS_COMPETITION];

        const skusWithTitle = pricingData.myProductTrendsCompetitionChartData
            ? chain(pricingData.myProductTrendsCompetitionChartData)
                  .filter(({ sku }) => sku !== selectedSKU)
                  .uniq(CommonWidgetMetrics.SKU)
                  .map(({ sku, sku_title }) => ({ sku, sku_title }))
                  .value()
            : [];

        const generateSection = (
            data: Partial<IMyProductTrendsOriginalData>[],
            tableData: TCrossChannelTableData[],
            expandedTableData: TCrossChannelTableData[],
            groupBy: string | null = null
        ) => ({
            charts: {
                [MyProductTrendsTypes.PRICE]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.PRICE,
                    groupBy
                ),
                [MyProductTrendsTypes.RPI]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.RPI,
                    groupBy
                ),
                [MyProductTrendsTypes.DEALS]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.DEALS,
                    groupBy
                ),
            },
            table: { tableData, expandedTableData },
        });

        return {
            [MyProductTrendsModules.CROSS_CHANNEL]: {
                channels: uniq(
                    pluck(
                        pricingData.myProductTrendsChartCrossChannelChartData,
                        CommonWidgetMetrics.CHANNEL_NAME
                    )
                ),
                ...generateSection(
                    pricingData.myProductTrendsChartCrossChannelChartData,
                    crossChannelTableData.tableData,
                    crossChannelTableData.expandedTableData
                ),
            },
            [MyProductTrendsModules.COMPETITION]: {
                skus: skusWithTitle,
                ...generateSection(
                    skuFilter(
                        pricingData.myProductTrendsCompetitionChartData,
                        competitonSku
                    ),
                    competitionTableData.tableData,
                    competitionTableData.expandedTableData,
                    CommonWidgetMetrics.SKU
                ),
            },
        };
    }, [
        pricingData,
        chartFilters[PricingCharts.PRICE_DEALS],
        chartFilters[PricingCharts.PRICE_DEALS_COMPETITION],
        crossChannelTableData,
        competitionTableData,
    ]);

    const additionalTitleComponent = (
        type: keyof typeof chartToggleFilters,
        moduleType?: string
    ) => {
        return (
            <div
                className={`d-flex align-items-center ${
                    moduleType === PricingCharts.PRICING_DEALS_TRENDS
                        ? "me-5"
                        : ""
                }`}
            >
                <Switch
                    id="pricing-trends-checkbox"
                    className="me-2"
                    checked={chartToggleFilters[type]}
                    size="small"
                    onChange={(checked: boolean) =>
                        setChartToggleFilters({ type, payload: checked })
                    }
                />
                <label htmlFor="pricing-trends-checkbox">PPU</label>
                {moduleType === PricingCharts.MS_RPI && (
                    <Radio.Group
                        size="small"
                        className="ms-4"
                        options={pricingFiltersOptions}
                        onChange={(e) =>
                            setChartFilters({
                                type: moduleType,
                                payload: e.target.value,
                            })
                        }
                        value={chartFilters[moduleType]}
                        optionType="button"
                        buttonStyle="solid"
                    />
                )}
                {moduleType === PricingCharts.PRODUCT_COMPETITION && (
                    <Select
                        className="ms-4"
                        mode="multiple"
                        showSearch
                        maxTagCount={1}
                        value={chartFilters[PricingCharts.PRICE_DEALS_SKUS]}
                        options={
                            pricingCompetitiveAnalysisData[
                                CommonChartAttributes.SKU_OPTIONS
                            ]
                        }
                        onChange={onChangePricingCompetitiveAnalysis}
                        maxTagTextLength={10}
                        allowClear
                        style={{ width: "26ch" }}
                    />
                )}
            </div>
        );
    };

    useEffect(() => {
        //! Note: The below code is to set the top 5 skus for the price-deals-skus filter

        const skusWithPrices = pricingData?.myProductPriceAndDeal?.map(
            (item: IPricingCompetitiveAnalysisOriginalData) => ({
                sku: item.sku,
                price: item.price ?? 0,
            })
        );

        const sortedSkus = sortBy(
            uniq(skusWithPrices, CommonWidgetMetrics.SKU),
            MyProductTrendsTypes.PRICE
        ).reverse();

        const topSkus = filter(sortedSkus, (item) => item.sku !== selectedSKU)
            .slice(0, 5)
            .map((item) => item.sku);

        setChartFilters({
            type: PricingCharts.PRICE_DEALS_SKUS,
            payload: topSkus,
        });

        setChartFilters({
            type: PricingCharts.PRICE_DEALS_COMPETITION,
            payload: topSkus,
        });
    }, [pricingData.myProductPriceAndDeal]);

    return (
        <WidgetModuleLayout
            loaderCondition={
                keys(pricingData).length === 0 ||
                loadingState?.[ProductDeepdiveModules.PRICING]
            }
        >
            <SummaryWidget
                widgetData={pricingData.priceRpiDeal}
                activeTab={ProductDeepdiveModules.PRICING}
            />
            <SatisfactionDriversSummaryWidget
                moduleType={ProductDeepdiveModules.PRICING}
                widgetData={{
                    ...pricingData.workingNotWorkingInsights,
                    aiSummary: pricingData.aiSummary,
                }}
            />
            <TrendsWithCompareModalWidget
                title="Pricing and %Deals Trends"
                widgetLinkName="Compare"
                widgetData={pricingAndDealsData}
                graphAttributes={{
                    yAxisTitle: `Price (${currencySymbol})`,
                    yAxisOppositeTitle: "% Deals",
                    yAxisOppositeDashStyleReq: true,
                }}
                onWidgetLinkHandler={() => setShowPricingAndDealsTrends(true)}
                {...{ additionalTitleComponent }}
            />
            <Row className="my-4" justify="center" align="middle" gutter={16}>
                <Col span={12}>
                    <PriceVsSalesWidget
                        title="Price Vs Sales"
                        widgetData={priceVsSalesData}
                        {...{ additionalTitleComponent, currencySymbol }}
                    />
                </Col>
                <Col span={12}>
                    <MsVsRpiTrends
                        title="MS Vs RPI"
                        widgetData={msVsRpiData}
                        {...{ additionalTitleComponent, currencySymbol }}
                    />
                </Col>
            </Row>
            <WidgetCard className="my-4">
                <Row className="fs-5 mb-5 w-100" justify="space-between">
                    <Col span={17}>
                        <h5>My Product Vs Competition</h5>
                    </Col>
                    {additionalTitleComponent && (
                        <Col span={7}>
                            {additionalTitleComponent(
                                PricingCharts.PRODUCT_COMPETITION_PPU,
                                PricingCharts.PRODUCT_COMPETITION
                            )}
                        </Col>
                    )}
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <PriceAndDealsCompetitionWidget
                            title="Price and %Deals"
                            widgetData={pricingCompetitiveAnalysisData.trend}
                            {...{ currencySymbol }}
                        />
                    </Col>
                    <Col span={12}>
                        <SalesVsPriceCompetitiveAnalysisWidget
                            widgetData={
                                pricingCompetitiveAnalysisData[
                                    CommonChartAttributes.BUBBLE_CHART
                                ]
                            }
                            title="Competitive Analysis Showing Sales vs Price"
                            {...{ currencySymbol }}
                        />
                    </Col>
                </Row>
            </WidgetCard>
            <CompetitiveProductsDaywiseTrends
                title="Daywise Trends of Competitive Products"
                widgetData={pricingData.datewiseTrendsOfCompetitiveProducts}
            />
            {showPricingAndDealsTrends && (
                <PricingAndDealCompetitionAnalysisWidget
                    title={`My Products Trends on ${CHANNEL_CONFIGS[channel].name}`}
                    widgetData={myProductTrendsData}
                    show={showPricingAndDealsTrends}
                    setShow={setShowPricingAndDealsTrends}
                    {...{ chartFilters, setChartFilters, currencySymbol }}
                />
            )}
        </WidgetModuleLayout>
    );
};

export default ProductAnalysisPricingModule;
